import React, { useEffect, useState } from 'react';
import { Card, Table, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { formatFreezeReasons, hasSuperuserAccess } from '../../../utils';

import { SUPERUSER_LEVEL } from '../../../constants';

import { teamApi, adminApi } from '../../../api';
import { useAlerts, useAuth } from '../../context';

import Loader from '../Loader';
import FreezeModal from '../FreezeModal';
import PaymentInstrumentModal from './PaymentInstrumentModal';

function PaymentMethods({ name, handle, redirect, piUuid }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAlert } = useAlerts();
  const { user } = useAuth();
  const [loaded, setLoaded] = useState(true);
  const [showFreezePaymentMethodModal, setShowFreezePaymentMethodModal] = useState(false);
  const [paymentInstrumentDetailsLoaded, setPaymentInstrumentDetailsLoaded] = useState(false);
  const [paymentInstrumentData, setPaymentInstrumentData] = useState(undefined);
  const [paymentInstruments, setPaymentInstruments] = useState([]);
  const [isLoadingPaymentInstruments, setIsLoadingPaymentInstruments] = useState(false);

  // this is required because this component is used for both superuser and non-superuser views
  const isAdmin = () => {
    return (
      hasSuperuserAccess(user, [SUPERUSER_LEVEL.FULL_ACCESS]) &&
      window.location.href.includes('/admin/')
    );
  };

  const disableFreeze = (pi) => {
    if (isAdmin()) return false;
    const freeze_reasons = Object.keys(pi.freeze_reasons);
    if (freeze_reasons.length === 0) return false;
    if (freeze_reasons.length === 1 && freeze_reasons[0] === 'by_customer') return false;
    return true;
  };

  const generateFreezePaymentMethodApiCall = (transaction_list, freezeReasonObj) => {
    const reasonsWithStatus = formatFreezeReasons(
      Object.keys(paymentInstrumentData.freeze_reasons),
      new Set(freezeReasonObj.freeze_reasons)
    );

    if (isAdmin()) {
      return () =>
        adminApi.paymentInstrumentFreezeUnfreeze(
          paymentInstrumentData.payment_method_id,
          user.currentTeam.id,
          !!transaction_list,
          reasonsWithStatus
        );
    }
    return () =>
      teamApi.paymentInstrumentFreezeUnfreeze(
        !paymentInstrumentData.frozen,
        paymentInstrumentData.payment_method_id,
        user.currentTeam.id,
        !!transaction_list
      );
  };

  const fetchPaymentInstruments = async (callback) => {
    try {
      let response;
      if (isAdmin()) {
        response = await adminApi.getEndUserPaymentInstruments({
          team: user.currentTeam.id,
          handle: handle,
        });
      } else {
        response = await teamApi.getEndUserPaymentInstruments({
          team: user.currentTeam.id,
          handle: handle,
        });
      }
      if (response.success) {
        setPaymentInstruments(response.payment_instruments);
        if (callback) callback(response);
        setIsLoadingPaymentInstruments(false);
      } else {
        redirect();
      }
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
      redirect();
    }
  };

  const handleSubmitFreezePayment = async (values, transaction_list = []) => {
    const freezePaymentApiCall = generateFreezePaymentMethodApiCall(transaction_list, values);

    const response = await freezePaymentApiCall();
    if (response.success) {
      setAlert({ message: response.message, variant: 'success' });
    }

    if (response.status_code === 400) {
      setAlert({
        message: response.message,
        variant: 'danger',
      });
    } else if (response.status_code === 409) {
      setAlert({
        message: response.message,
        variant: 'warning',
      });
    }

    fetchPaymentInstruments();
    setShowFreezePaymentMethodModal(false);
    setPaymentInstrumentData(undefined);
    setLoaded(true);
  };

  const getPaymentInstrument = async (id, freeze_reasons) => {
    if (id) {
      setLoaded(false);
      try {
        let response;
        if (isAdmin()) {
          response = await adminApi.getPaymentInstrument(id);
        } else {
          response = await teamApi.getPaymentInstrument(id, user.currentTeam.id);
        }
        setPaymentInstrumentData({ ...response.payment_method, freeze_reasons: freeze_reasons });
        setPaymentInstrumentDetailsLoaded(true);
      } catch (error) {
        setAlert({ message: error, variant: 'danger' });
        setPaymentInstrumentDetailsLoaded(false);
        setPaymentInstrumentData(undefined);
      } finally {
        setLoaded(true);
      }
    } else {
      setPaymentInstrumentDetailsLoaded(false);
      setPaymentInstrumentData(undefined);
    }
  };

  useEffect(() => {
    if (handle) {
      setIsLoadingPaymentInstruments(true);
      fetchPaymentInstruments();
    }
  }, [handle]);

  useEffect(() => {
    if (piUuid && paymentInstruments.length > 0) {
      const pi = paymentInstruments.find((p) => p.payment_method_id === piUuid);
      if (pi) {
        getPaymentInstrument(pi.payment_method_id, pi.freeze_reasons);
      } else {
        const basePath = isAdmin() ? '/admin/end_user' : '/settings/end_user';
        navigate(`${basePath}/${handle}/payment-instruments`);
      }
    }
  }, [piUuid, paymentInstruments]);

  if (isLoadingPaymentInstruments) return <Loader />;

  return (
    <>
      <h3 className="mb-2">{t('common:paymentMethods.title')}</h3>
      <p className="text-meta mb-5">{t('common:paymentMethods.description')}</p>
      <Card className="mb-2 border overflow-hidden">
        {!loaded && <Loader />}
        <Table responsive>
          <thead>
            <tr>
              <th className="border-end">{t('common:labels.name')}</th>
              <th className="border-end">{t('common:paymentMethods.labels.method')}</th>
              <th className="border-end">{t('common:paymentMethods.labels.identifiers')}</th>
              <th className="border-end">{t('common:paymentMethods.labels.status')}</th>
              <th className="text-center" width="20%">
                {t('common:labels.action')}
              </th>
            </tr>
          </thead>
          <tbody>
            {paymentInstruments.map((pi) => (
              <tr key={pi.payment_method_id}>
                <td className={`border-end ${pi.payment_name ? '' : 'text-info'}`}>
                  {pi.payment_name || t('common:labels.noName')}
                </td>
                <td className="border-end">{pi.payment_method_type}</td>
                <td className="border-end">
                  <div>
                    {pi.payment_instrument_id || (
                      <div className="text-info">{t('common:labels.na')}</div>
                    )}

                    {pi.payment_method_type !== 'Bank Account' || (
                      <div className="mt-3">{`${t(
                        'common:paymentMethods.labels.accountNumber'
                      )}: ${pi.identifier.slice(-5)}`}</div>
                    )}
                  </div>
                </td>
                <td className={`border-end${pi.frozen ? ' text-danger' : ''}`}>
                  {!pi.frozen ? t('common:status.enabled') : t('common:status.disabled')}
                </td>
                <td className="text-center text-nowrap" width="25%">
                  <Button
                    className="p-0 text-lg text-info hover me-4"
                    variant="link"
                    onClick={() => {
                      const basePath = isAdmin() ? '/admin/end_user' : '/settings/end_user';
                      navigate(`${basePath}/${handle}/payment-instruments/${pi.payment_method_id}`);
                    }}
                  >
                    <i className="fas fa-eye" />
                  </Button>
                  <Button
                    variant="link text-underline"
                    onClick={() => {
                      setPaymentInstrumentData(pi);
                      setShowFreezePaymentMethodModal(true);
                    }}
                    className="p-0"
                    disabled={disableFreeze(pi)}
                  >
                    {isAdmin()
                      ? t(`common:freezeUnfreeze.modal.modify.button`)
                      : t(`common:buttons.${pi.frozen === true ? 'unfreeze' : 'freeze'}`)}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {showFreezePaymentMethodModal && (
        <FreezeModal
          show={showFreezePaymentMethodModal}
          data={paymentInstrumentData}
          name={paymentInstrumentData.payment_name || t('common:labels.noName')}
          handleFreeze={handleSubmitFreezePayment}
          setShow={setShowFreezePaymentMethodModal}
          isAdmin={isAdmin()}
          type={t('common:labels.paymentInstrument')}
          reasons={isAdmin() ? paymentInstrumentData.freeze_reasons : {}}
          piOwnerName={name}
        />
      )}
      {paymentInstrumentDetailsLoaded && paymentInstrumentData && (
        <PaymentInstrumentModal
          show={paymentInstrumentDetailsLoaded}
          data={paymentInstrumentData}
          onHide={() => {
            const basePath = isAdmin() ? '/admin/end_user' : '/settings/end_user';
            navigate(`${basePath}/${handle}/payment-instruments`, { replace: true });
            setPaymentInstrumentDetailsLoaded(false);
            setPaymentInstrumentData(undefined);
          }}
        />
      )}
    </>
  );
}

PaymentMethods.propTypes = {
  /**
   * The values of the End-User form
   */
  handle: PropTypes.string.isRequired,
};

export default PaymentMethods;
